// src/features/Navigation/components/NavigationEditPanel/NavigationEditPanel.tsx
import { useEffect, useRef, useState } from "react";
import { useForm, useFormState, Controller } from "react-hook-form";
import { ArrowLeft } from "lucide-react";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { Switch } from "src/components/ui/switch";
import { Label } from "src/components/ui/label";
import { Button } from "src/components/ui/button";
import { safeImageUrl } from "src/utils/safeImageUrl";

interface NavigationEditPanelProps {
	item: NavigationItem | null;
	allItems: NavigationItem[];
	onSave: (
		values: NavigationItemFormValues,
		imageFile: File | null,
		imageRemoved: boolean
	) => void;
	onDelete: (id: string) => void;
	onClose: () => void;
	isSaving: boolean;
}

function getDescendantIds(
	itemId: string,
	items: NavigationItem[]
): Set<string> {
	const ids = new Set<string>();
	const collectChildren = (parentId: string) => {
		for (const item of items) {
			if (item.parent === parentId && !ids.has(item.id)) {
				ids.add(item.id);
				collectChildren(item.id);
			}
		}
	};
	collectChildren(itemId);
	return ids;
}

const NavigationEditPanel = ({
	item,
	allItems,
	onSave,
	onDelete,
	onClose,
	isSaving
}: NavigationEditPanelProps) => {
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const isNew = !item?.id;

	const defaultValues: NavigationItemFormValues = {
		name: item?.name ?? "",
		description: item?.description ?? "",
		permalink: item?.permalink ?? "", // Field hidden from form but preserved for existing items
		parent: item?.parent ?? "",
		url: item?.url ?? "",
		openIn: item?.web ? "web" : "webview",
		published: item?.published ?? false,
		layout: item?.layout ?? "row"
	};

	const { control, handleSubmit, register, reset, watch } =
		useForm<NavigationItemFormValues>({
			defaultValues,
			mode: "onChange"
		});

	const { isDirty, errors } = useFormState({ control });

	// Reset form when selected item changes
	useEffect(() => {
		reset(defaultValues);
		setImageFile(null);
		setImagePreview(null);
		setImageRemoved(false);
		setImageError(null);
	}, [item?.id, item?.parent]);

	// Clean up preview URL
	useEffect(() => {
		return () => {
			if (imagePreview) URL.revokeObjectURL(imagePreview);
		};
	}, [imagePreview]);

	const handleImageChange = (file: File) => {
		const validTypes = ["image/jpeg", "image/png"];
		if (!validTypes.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed");
			return;
		}
		if (file.size > 1024 * 1024) {
			setImageError("Image must be under 1MB");
			return;
		}
		setImageError(null);
		setImageFile(file);
		setImageRemoved(false);
		if (imagePreview) URL.revokeObjectURL(imagePreview);
		setImagePreview(URL.createObjectURL(file));
	};

	const handleRemoveImage = () => {
		setImageFile(null);
		if (imagePreview) URL.revokeObjectURL(imagePreview);
		setImagePreview(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const onSubmit = (values: NavigationItemFormValues) => {
		onSave(values, imageFile, imageRemoved);
	};

	// Filter parent options: exclude self and descendants
	const excludeIds = item?.id
		? new Set([item.id, ...getDescendantIds(item.id, allItems)])
		: new Set<string>();
	const parentOptions = allItems.filter((i) => !excludeIds.has(i.id));

	const existingImageUrl =
		item?.image && !imageRemoved ? safeImageUrl(item.image) : null;
	const displayImage = imagePreview || existingImageUrl;

	const preventEnterSubmit = (e: React.KeyboardEvent) => {
		const target = e.target as HTMLElement;
		if (e.key === "Enter" && target.tagName === "INPUT") e.preventDefault();
	};

	const canSave = isNew ? true : isDirty || imageFile !== null || imageRemoved;

	return (
		<div className="h-full flex flex-col gap-4">
			<div className="flex items-center gap-3 h-9">
				<button
					onClick={onClose}
					className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
				>
					<ArrowLeft className="h-4 w-4" />
				</button>
				<label className="font-heading text-lg font-medium text-foreground">
					{isNew ? "New Item" : "Edit Item"}
				</label>
			</div>

			<form
				onSubmit={handleSubmit(onSubmit)}
				onKeyDown={preventEnterSubmit}
				className="flex-1 overflow-y-auto rounded-xl border border-border bg-subtle shadow-sm flex flex-col"
			>
				<div className="p-6 flex flex-col gap-4">
					{/* Name */}
					<div>
						<Label
							htmlFor="name"
							className="text-sm font-medium text-zinc-600 mb-1.5 block"
						>
							Name *
						</Label>
						<Input
							id="name"
							{...register("name", {
								required: "Name is required",
								maxLength: {
									value: 255,
									message: "Name must be 255 characters or fewer"
								}
							})}
							placeholder="Menu item label"
						/>
						{errors.name && (
							<span className="text-xs text-red-500 mt-1">
								{errors.name.message}
							</span>
						)}
					</div>

					{/* Description */}
					<div>
						<Label
							htmlFor="description"
							className="text-sm font-medium text-zinc-600 mb-1.5 block"
						>
							Description
						</Label>
						<Textarea
							id="description"
							{...register("description", {
								maxLength: {
									value: 255,
									message: "Description must be 255 characters or fewer"
								}
							})}
							placeholder="Optional subtitle text"
							rows={2}
						/>
					</div>

					{/* Parent */}
					<div>
						<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
							Parent
						</Label>
						<select
							{...register("parent")}
							className="flex h-9 w-full items-center rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
						>
							<option value="">None (top level)</option>
							{parentOptions.map((opt) => (
								<option key={opt.id} value={opt.id}>
									{opt.name}
								</option>
							))}
						</select>
					</div>

					{/* Layout */}
					<div>
						<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
							Layout
						</Label>
						<select
							{...register("layout")}
							className="flex h-9 w-full items-center rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
						>
							<option value="row">Row</option>
							<option value="grid">Grid</option>
						</select>
					</div>

					{/* URL + Open in — only for non grid layouts */}
					{watch("layout") !== "grid" && (
						<>
							<div>
								<Label
									htmlFor="url"
									className="text-sm font-medium text-zinc-600 mb-1.5 block"
								>
									URL
								</Label>
								<p className="text-[13px] text-muted-foreground mb-2">
									Leave blank to use this item as a parent label
								</p>
								<Input id="url" {...register("url")} placeholder="google.com" />
							</div>

							{watch("url") && (
								<div>
									<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
										Open in
									</Label>
									<select
										{...register("openIn")}
										className="flex h-9 w-full items-center rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
									>
										<option value="webview">App (Webview)</option>
										<option value="web">System Browser (Web)</option>
									</select>
								</div>
							)}
						</>
					)}

					{/* Published */}
					<div className="flex items-center gap-3">
						<Controller
							control={control}
							name="published"
							render={({ field }) => (
								<Switch
									id="published"
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							)}
						/>
						<Label
							htmlFor="published"
							className="text-sm font-medium text-zinc-600 cursor-pointer"
						>
							Published
						</Label>
					</div>

					{/* Image upload — only shown for grid layout */}
					{watch("layout") === "grid" && (
						<div>
							<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
								Image
							</Label>
							<p className="text-[13px] text-muted-foreground mb-2">
								Suggested: 513x300px, under 1MB, JPG or PNG
							</p>
							{displayImage ? (
								<div className="relative rounded-lg overflow-hidden border border-border">
									<img
										src={displayImage}
										alt="Preview"
										className="w-full h-auto object-cover"
									/>
									<div className="absolute top-2 right-2 flex gap-1">
										<button
											type="button"
											onClick={() => fileInputRef.current?.click()}
											className="bg-white/90 text-sm px-2 py-1 rounded-md hover:bg-white transition-colors"
										>
											Change
										</button>
										<button
											type="button"
											onClick={handleRemoveImage}
											className="bg-white/90 text-sm px-2 py-1 rounded-md hover:bg-white text-red-600 transition-colors"
										>
											Remove
										</button>
									</div>
								</div>
							) : (
								<div
									onClick={() => fileInputRef.current?.click()}
									onDragOver={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}}
									onDrop={(e) => {
										e.preventDefault();
										e.stopPropagation();
										const file = e.dataTransfer.files[0];
										if (file) handleImageChange(file);
									}}
									className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/40 transition-colors"
								>
									<p className="text-sm text-muted-foreground">
										Click or drag an image here
									</p>
								</div>
							)}
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/png"
								className="hidden"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) handleImageChange(file);
								}}
							/>
							{imageError && (
								<span className="text-xs text-red-500 mt-1">{imageError}</span>
							)}
						</div>
					)}
				</div>
				{/* Actions */}
				<div className="flex items-center justify-between p-6 border-t border-border">
					{!isNew && (
						<Button
							type="button"
							variant="destructive"
							onClick={() => onDelete(item!.id)}
						>
							Delete
						</Button>
					)}
					<div className={isNew ? "ml-auto" : ""}>
						<Button type="submit" disabled={!canSave || isSaving}>
							{isSaving ? (
								<span className="flex items-center gap-2">
									<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
									Saving...
								</span>
							) : isNew ? (
								"Create"
							) : (
								"Save"
							)}
						</Button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default NavigationEditPanel;
